import { escapeHtml } from './html'

export const SUPPORT_WINDOW_SECONDS = 60 * 60
export const SUPPORT_MESSAGES_PER_WINDOW = 10
export const SUPPORT_MESSAGE_MAX_LENGTH = 5000
export const SUPPORT_SUBJECT_MAX_LENGTH = 80

export const SUPPORT_RATINGS = ['very happy', 'happy', 'sad', 'very sad'] as const

export type SupportKind = 'question' | 'feedback'
export type SupportRating = (typeof SUPPORT_RATINGS)[number]

export type SupportMessage = {
  kind: SupportKind
  message: string
  rating?: SupportRating
  page?: string
  sender: { email: string; name?: string | null }
  team?: { name: string; slug: string }
}

export const countRecentSupportMessages = (requests: { at: number }[], now = Date.now()) =>
  requests.filter((request) => now - request.at < SUPPORT_WINDOW_SECONDS * 1000).length

export const supportLimitReached = (recent: number) => recent > SUPPORT_MESSAGES_PER_WINDOW

export const isEmptySupportMessage = ({ kind, message, rating }: Pick<SupportMessage, 'kind' | 'message' | 'rating'>) =>
  !message.trim() && (kind === 'question' || !rating)

const firstLine = (message: string) => message.trim().split('\n')[0].trim()

const truncate = (value: string, max: number) => (value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value)

export const supportSubject = ({ kind, message, rating, team }: SupportMessage) => {
  if (kind === 'feedback') {
    const from = team ? ` from ${team.name}` : ''
    const mood = rating ? ` (${rating})` : ''
    return `Feedback${from}${mood}`
  }
  return truncate(firstLine(message) || 'Question', SUPPORT_SUBJECT_MAX_LENGTH)
}

const contextLines = ({ kind, rating, page, sender, team }: SupportMessage) => {
  const lines: [string, string][] = [['From', sender.name ? `${sender.name} <${sender.email}>` : sender.email]]
  if (team) lines.push(['Team', `${team.name} (${team.slug})`])
  if (page) lines.push(['Page', page])
  if (kind === 'feedback') lines.push(['Rating', rating ?? 'none'])
  return lines
}

export const supportEmailBody = (input: SupportMessage) => {
  const message = input.message.trim()
  const context = contextLines(input)
  const text = [message, '', '--', ...context.map(([label, value]) => `${label}: ${value}`)].join('\n')
  const html = [
    `<p style="white-space:pre-wrap">${escapeHtml(message)}</p>`,
    '<hr>',
    `<p style="color:#6b7382">${context.map(([label, value]) => `${label}: ${escapeHtml(value)}`).join('<br>')}</p>`
  ].join('\n')
  return { text, html }
}
